import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Setup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
    if (form.password !== form.confirm) return setError("Les mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/setup", { email: form.email, password: form.password, name: form.name || null });
      localStorage.setItem("ced_token", data.token);
      setUser(data.user);
      toast.success("Compte propriétaire créé");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.08),transparent_40%)]" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20">
            <ShieldCheck className="w-8 h-8 text-black" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 tracking-tight">Première configuration</h1>
          <p className="text-sm text-slate-500 mt-1 text-center">Créez votre compte propriétaire (accès complet). Cet écran ne sera plus accessible ensuite.</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-5" data-testid="setup-form">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-700">Nom (facultatif)</Label>
            <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cédric" data-testid="setup-name" className="bg-slate-50 border-slate-200 h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700">Adresse email</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="setup-email" className="bg-slate-50 border-slate-200 h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
            <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="setup-password" className="bg-slate-50 border-slate-200 h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm" className="text-slate-700">Confirmer le mot de passe</Label>
            <Input id="confirm" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required data-testid="setup-confirm" className="bg-slate-50 border-slate-200 h-11" />
          </div>
          {error && <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="setup-error">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="btn-setup" className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Créer mon compte
          </Button>
        </form>
      </div>
    </div>
  );
}
